/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Route from '@ember/routing/route';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import type AutomationFinAIAgentContentController from 'embercom/controllers/apps/app/automation/fin-ai-agent/content';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type AppService from 'embercom/services/app-service';
import type UnifiedContentStateService from 'embercom/services/unified-content-state';
import KnowledgeHubApi, {
  type KnowledgeHubApiQueryParams,
} from 'embercom/lib/knowledge-hub/list-api';
import { getOwner } from '@ember/application';
import { ContentWrapperType } from 'embercom/models/content-service/content-wrapper';
import { type SortDirection } from 'embercom/lib/knowledge-hub/constants';
import { type ContentSearchParams } from 'embercom/lib/content-service/search-api';
import { FolderFilterOption } from 'embercom/lib/content-service/search-api';
import type Transition from '@ember/routing/transition';

export default class AutomationFinAIAgentContentRoute extends Route {
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare appService: AppService;
  @service declare unifiedContentState: UnifiedContentStateService;

  queryParams = {
    nativeContent: {
      refreshModel: true,
    },
  };

  analytics = {
    section: 'automation',
    place: 'fin-ai-agent.content',
  };

  get titleToken() {
    return this.intl.t('ai-agent.train.content');
  }

  async beforeModel() {
    await this.knowledgeHubService.fetchFoldersOnce();
    await this.knowledgeHubService.fetchKnowledgeUsageSummary();
  }

  async model(params: KnowledgeHubApiQueryParams): Promise<KnowledgeHubApi> {
    let defaultSearchParams: ContentSearchParams = {
      object_types: this.knowledgeHubService.getSearchObjectTypes(),
      content_wrapper_types: [ContentWrapperType.SUPPORT_CONTENT],
      app_id: this.appService.app.id as string,
      folder_filter_option: FolderFilterOption.None,
      sort_by: 'title',
      sort_direction: 'asc' as SortDirection,
      ...(this.appService.app.canUseUnifiedContentUI && {
        native_content: false,
      }),
    };

    this.knowledgeHubService.api = new KnowledgeHubApi(getOwner(this), defaultSearchParams, params);
    this.knowledgeHubService.api.loadPage();
    return this.knowledgeHubService.api;
  }

  setupController(
    controller: AutomationFinAIAgentContentController,
    model: KnowledgeHubApi,
    transition: Transition,
  ) {
    super.setupController(controller, model, transition);

    if (this.appService.app.canUseUnifiedContentUI) {
      if (controller.nativeContent === undefined) {
        controller.set('nativeContent', false);
      }
      // Register the new API with the state service
      this.unifiedContentState.registerListApi(model);
    }
  }

  resetController(controller: AutomationFinAIAgentContentController, isExiting: boolean) {
    if (isExiting) {
      controller.set('openSalesforceSync', undefined);
      controller.set('salesforceSyncStep', undefined);
    }
  }
}
